/**
 * @file Helper único para construir el desglose de precio que ve el cliente.
 *
 * Se usa en el Detalle de Producto (modal "Desglose de Costos") y refleja las
 * mismas líneas que la cotización final (PDF). Centralizar aquí evita que las
 * vistas se desincronicen.
 *
 * 🧾 IVA UNIFICADO: el desglose muestra UNA sola línea "IVA (19%)" que suma
 * todos los IVAs (Mandante + Comisión + Gastos Operacionales). La suma de las
 * líneas SIEMPRE cuadra con el Total a Pagar.
 */

const toNumber = (value) => Number(value || 0);

/**
 * Construye las líneas del desglose a partir del `priceBreakdown` del producto.
 *
 * @param {Object} priceBreakdown - Desglose del backend (product.priceBreakdown).
 * @returns {Array<{ key: string, label: string, value: number }>}
 */
export function buildPriceBreakdownRows(priceBreakdown = {}) {
  const pb = priceBreakdown || {};
  const vatPercentage = toNumber(pb.vatPercentage) || 19;

  const netPrice = toNumber(pb.netPrice);
  const transferTax = toNumber(pb.transferTax);
  const transferFee = toNumber(pb.transferFee);
  const additionalCosts = Array.isArray(pb.additionalCosts)
    ? pb.additionalCosts
    : [];

  // 🆕 IVA UNIFICADO = suma de todos los IVAs (Mandante + Comisión + Gastos Op.).
  // Fallback: netPriceWithVat - netPrice (da el mismo resultado). Así la suma de
  // las líneas siempre es igual al Total a Pagar.
  const ivaSum =
    toNumber(pb.ownerPriceVat) +
    toNumber(pb.commissionVat) +
    toNumber(pb.operationalCostsVat);
  const ivaTotal = ivaSum > 0 ? ivaSum : toNumber(pb.netPriceWithVat) - netPrice;

  const rows = [{ key: "netPrice", label: "Valor Neto:", value: netPrice }];

  if (ivaTotal > 0) {
    rows.push({
      key: "iva",
      label: `IVA (${vatPercentage}%):`,
      value: ivaTotal,
    });
  }
  if (transferTax > 0) {
    rows.push({
      key: "transferTax",
      label: "Impuesto Transferencia:",
      value: transferTax,
    });
  }
  if (transferFee > 0) {
    rows.push({
      key: "transferFee",
      label: "Costo Transferencia:",
      value: transferFee,
    });
  }

  additionalCosts.forEach((cost, index) => {
    const value = toNumber(cost.value);
    if (value > 0) {
      rows.push({ key: `additional-${index}`, label: `${cost.name}:`, value });
    }
  });

  return rows;
}

/**
 * Total a Pagar del desglose. Usa el `totalPrice` del backend si viene; si no,
 * suma las líneas generadas.
 *
 * @param {Object} priceBreakdown
 * @param {Array} [rows] - Filas ya construidas (opcional, evita recomputar).
 * @returns {number}
 */
export function getPriceBreakdownTotal(priceBreakdown = {}, rows) {
  const pb = priceBreakdown || {};
  if (pb.totalPrice) return toNumber(pb.totalPrice);
  const list = rows || buildPriceBreakdownRows(pb);
  return list.reduce((sum, row) => sum + toNumber(row.value), 0);
}
